export const POPUP_PANEL_DEFAULT_WIDTH = 400;

export const EDITOR_TYPES = {
  POPUP: 'popup',
  NODE_BODY: 'nodeBody',
  NODE_TITLE: 'nodeTitle',
  EDGE: 'edge',
  CALCULATOR_LABEL: 'calculatorLabel',
  AUTOMATION_QUESTION: 'automationQuestion',
  AUTOMATION_START: 'automationStart',
  DOCUMENT: 'document',
  // Named for the capability, not the feature, so any plain prose field can reuse them.
  RICH_TEXT_BASIC: 'richTextBasic',
  RICH_TEXT_BASIC_LINKS: 'richTextBasicLinks'
} as const;

export type EditorType = (typeof EDITOR_TYPES)[keyof typeof EDITOR_TYPES];

export const TIPTAP_PRIORITY = {
  DEFAULT: 100,
  NESTED_EXTENSION: 1100
} as const;

export const TIPTAP_TYPES = {
  BOLD: 'bold',
  BULLET_LIST: 'bulletList',
  DATA_ELEMENT: 'dataElement',
  EHR_LINK: 'ehrLink',
  FILE_LINK: 'fileLink',
  HEADING: 'heading',
  IMAGE: 'image',
  ITALIC: 'italic',
  LINK: 'link',
  LIST_ITEM: 'listItem',
  ORDER_GROUP_CONTENT: 'orderGroupContent',
  ORDER_GROUP_PARAGRAPH: 'orderGroupParagraph',
  ORDER_GROUP_TITLE: 'orderGroupTitle',
  ORDER_GROUP: 'orderGroup',
  ORDER: 'order',
  ORDERED_LIST: 'orderedList',
  PARAGRAPH: 'paragraph',
  POPUP_LABEL: 'popupLabel',
  POPUP_PANEL_BODY_PARAGRAPH: 'popupPanelBodyParagraph',
  POPUP_PANEL_BODY: 'popupPanelBody',
  POPUP_PANEL_HEADER_PARAGRAPH: 'popupPanelHeaderParagraph',
  POPUP_PANEL_HEADER: 'popupPanelHeader',
  POPUP_PANEL: 'popupPanel',
  POPUP: 'popup',
  QUESTION_CONTENT: 'questionContent',
  QUESTION_LABEL_PARAGRAPH: 'questionLabelParagraph',
  QUESTION_LABEL: 'questionLabel',
  QUESTION_OPTION_PARAGRAPH: 'questionOptionParagraph',
  QUESTION_OPTION: 'questionOption',
  QUESTION: 'question',
  SUBSCRIPT: 'subscript',
  SUPERSCRIPT: 'superscript',
  TABLE_CELL: 'tableCell',
  TABLE_HEADER: 'tableHeader',
  TABLE_ROW: 'tableRow',
  TABLE: 'table',
  TEXT: 'text',
  UNDERLINE: 'underline'
} as const;

export type TiptapType = (typeof TIPTAP_TYPES)[keyof typeof TIPTAP_TYPES];

/**
 * The custom inline marks: each turns its text into an interactive affordance. They are mutually
 * exclusive — a span carries at most one, so the bubble menu offers only the button of the mark
 * already there and anything that slips past it keeps the existing mark (PRS-8517). Kept as one
 * list so the policies below can't drift apart.
 */
export const CUSTOM_MARK_TYPES = [
  TIPTAP_TYPES.ORDER,
  TIPTAP_TYPES.POPUP,
  TIPTAP_TYPES.DATA_ELEMENT,
  TIPTAP_TYPES.FILE_LINK,
  TIPTAP_TYPES.EHR_LINK,
  TIPTAP_TYPES.LINK
] as const;

/**
 * Custom marks that must wrap at least one non-whitespace/punctuation character. An instance
 * covering only whitespace/punctuation (e.g. " ", ",", "()") is auto-removed during editing
 * (PRS-7921). A lone icon/symbol (e.g. a popup linked from "ⓘ") counts as content (PRS-8661).
 */
export const CONTENT_REQUIRED_MARK_TYPES = CUSTOM_MARK_TYPES;

/**
 * Block node types that participate in TipTap text alignment (`configureTextAlign` in @agilemd/text-editor).
 */
export const TEXT_ALIGN_NODE_TYPES = [
  TIPTAP_TYPES.PARAGRAPH,
  TIPTAP_TYPES.HEADING,
  TIPTAP_TYPES.QUESTION_LABEL_PARAGRAPH,
  TIPTAP_TYPES.POPUP_PANEL_HEADER_PARAGRAPH
] as const;

/** Returns a fresh empty tiptap doc with a single paragraph. Use for body, node title, edge label, etc. */
export function createEmptyTiptapDoc() {
  return {
    type: 'doc' as const,
    content: [{ type: TIPTAP_TYPES.PARAGRAPH }]
  };
}

/** Returns a fresh empty tiptap doc with a single popup panel header paragraph. Use for popup header content. */
export function createEmptyPopupHeaderDoc() {
  return {
    type: 'doc' as const,
    content: [{ type: TIPTAP_TYPES.POPUP_PANEL_HEADER_PARAGRAPH }]
  };
}

/** Returns a fresh empty v3 popup content doc (header + body). Use for new popup entries. */
export function createEmptyPopupContentDoc() {
  return {
    type: 'doc' as const,
    content: [
      {
        type: TIPTAP_TYPES.POPUP_PANEL_HEADER,
        content: [{ type: TIPTAP_TYPES.POPUP_PANEL_HEADER_PARAGRAPH }]
      },
      {
        type: TIPTAP_TYPES.POPUP_PANEL_BODY,
        content: [{ type: TIPTAP_TYPES.PARAGRAPH }]
      }
    ]
  };
}

export const TIPTAP_GROUPS = {
  ALLOWED_IN_POPUP: 'allowedInPopup',
  ALLOWED_IN_POPUP_LABEL: 'allowedInPopupLabel',
  ALLOWED_IN_TABLE: 'allowedInTable',
  ALLOWED_IN_ORDER_GROUP: 'allowedInOrderGroup',
  ALLOWED_IN_QUESTION_OPTION: 'allowedInQuestionOption',
  ALLOWED_IN_QUESTION_LABEL: 'allowedInQuestionLabel'
} as const;

export const TIPTAP_ALLOWED_MARKS = {
  [TIPTAP_TYPES.ORDER_GROUP_TITLE]: ['bold', 'italic', 'underline'],
  [TIPTAP_TYPES.ORDER_GROUP_CONTENT]: ['bold', 'italic', 'underline', 'order']
};

export const VIEW_MODES = {
  AUTHOR: 'author',
  EHR: 'ehr',
  REVIEWER: 'reviewer',
  STATIC: 'static',
  VIEWER: 'viewer'
} as const;

export type ViewMode = (typeof VIEW_MODES)[keyof typeof VIEW_MODES];

/**
 * Table cell background color definitions.
 * Each color includes a name (for tooltips), a key (for test IDs), and color values.
 */
export interface TableCellColor {
  name: string;
  key: string;
  backgroundColor: string;
  borderColor: string;
}

export const TABLE_CELL_COLORS: readonly TableCellColor[] = Object.freeze([
  {
    name: 'Gray',
    key: 'gray',
    backgroundColor: '#fafafa',
    borderColor: '#bdbdbd'
  },
  {
    name: 'Blue',
    key: 'blue',
    backgroundColor: '#f1fdff',
    borderColor: '#77c9f2'
  },
  {
    name: 'Yellow',
    key: 'yellow',
    backgroundColor: '#fffdd8',
    borderColor: '#ffe171'
  },
  {
    name: 'Orange',
    key: 'orange',
    backgroundColor: '#ffe5c8',
    borderColor: '#ffb573'
  },
  {
    name: 'Red',
    key: 'red',
    backgroundColor: '#ffe6ea',
    borderColor: '#ff8d80'
  },
  {
    name: 'Green',
    key: 'green',
    backgroundColor: '#e5f7cd',
    borderColor: '#6cba08'
  },
  {
    name: 'Purple',
    key: 'purple',
    backgroundColor: '#f2f0fa',
    borderColor: '#7b69d4'
  },
  {
    name: 'Brown',
    key: 'brown',
    backgroundColor: '#f7f0f1',
    borderColor: '#b36d72'
  }
]);

/**
 * Get the backgroundColor value for a given color key.
 * Returns the backgroundColor string if found, or null if not found.
 *
 * @param key - Color key (e.g., "blue", "red", "yellow")
 * @returns Hex color string or null if key is invalid/not found
 */
export function getBackgroundColorForKey(key: string | null): string | null {
  if (!key) {
    return null;
  }

  const color = TABLE_CELL_COLORS.find(c => c.key === key);

  if (!color) {
    // Log warning for invalid color keys to help detect data corruption
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[getBackgroundColorForKey] Invalid color key "${key}". Valid keys are: ${TABLE_CELL_COLORS.map(
          c => c.key
        ).join(', ')}`
      );
    }

    return null;
  }

  return color.backgroundColor ?? null;
}

export interface SpecialCharacter {
  symbol: string;
  shortcut: string;
  title: string;
  key: string;
}

/**
 * Special character definitions with symbol, shortcut, and title.
 * This is the single source of truth for all special character metadata.
 *
 * Shortcuts are automatically replaced as you type:
 * - Symbol-based shortcuts (e.g., `->`, `>=`) can be used directly
 * - Word-based shortcuts use a backslash prefix (e.g., `\deg`, `\alpha`) to avoid
 *   interfering with normal word typing
 *
 * Examples:
 * - Type `->` to get `→`
 * - Type `\deg` to get `°`
 * - Type `\alpha` to get `α`
 * - Type `deg` or `alpha` normally without conversion
 */
export const SPECIAL_CHARACTERS: readonly SpecialCharacter[] = Object.freeze([
  // Row 1: Arrows and checkmark
  { symbol: '→', shortcut: '->', title: 'Right arrow', key: 'right-arrow' },
  { symbol: '←', shortcut: '<-', title: 'Left arrow', key: 'left-arrow' },
  { symbol: '↑', shortcut: '\\up', title: 'Up arrow', key: 'up-arrow' },
  { symbol: '↓', shortcut: '\\down', title: 'Down arrow', key: 'down-arrow' },
  { symbol: '✓', shortcut: '\\check', title: 'Checkmark', key: 'checkmark' },
  // Row 2: Comparison operators
  { symbol: '≥', shortcut: '>=', title: 'Greater than or equal', key: 'greater-than-equal' },
  { symbol: '≤', shortcut: '<=', title: 'Less than or equal', key: 'less-than-equal' },
  { symbol: '≠', shortcut: '!=', title: 'Not equal', key: 'not-equal' },
  { symbol: '≈', shortcut: '\\approx', title: 'Approximately equal', key: 'approximately-equal' },
  { symbol: '±', shortcut: '+/-', title: 'Plus or minus', key: 'plus-minus' },
  // Row 3: Greek letters
  { symbol: 'α', shortcut: '\\alpha', title: 'Alpha', key: 'alpha' },
  { symbol: 'β', shortcut: '\\beta', title: 'Beta', key: 'beta' },
  { symbol: 'γ', shortcut: '\\gamma', title: 'Gamma', key: 'gamma' },
  { symbol: 'Δ', shortcut: '\\delta', title: 'Delta', key: 'delta' },
  { symbol: 'μ', shortcut: '\\mu', title: 'Mu', key: 'mu' },
  // Row 4: Math operations and symbols
  { symbol: '×', shortcut: '\\times', title: 'Multiplication', key: 'multiplication' },
  { symbol: '÷', shortcut: '\\div', title: 'Division', key: 'division' },
  { symbol: '°', shortcut: '\\deg', title: 'Degree', key: 'degree' },
  { symbol: '©', shortcut: '(c)', title: 'Copyright', key: 'copyright' },
  { symbol: '®', shortcut: '(r)', title: 'Registered trademark', key: 'registered-trademark' }
]);

/**
 * Default shortcuts mapping for TipTap extension compatibility.
 * Derived from SPECIAL_CHARACTERS to ensure consistency.
 */
export const DEFAULT_SHORTCUTS: Record<string, string> = Object.fromEntries(
  SPECIAL_CHARACTERS.map(char => [char.shortcut, char.symbol])
);

/**
 * Array of all special character symbols, derived from SPECIAL_CHARACTERS.
 * Maintained for backward compatibility.
 */
export const SPECIAL_CHARACTERS_ARRAY = Object.freeze(
  SPECIAL_CHARACTERS.map(char => char.symbol)
) as readonly string[];

/**
 * Reverse mapping from character symbol to its keyboard shortcut.
 * Derived from SPECIAL_CHARACTERS to ensure consistency.
 */
export const CHAR_TO_SHORTCUT: Record<string, string> = Object.fromEntries(
  SPECIAL_CHARACTERS.map(char => [char.symbol, char.shortcut])
);

/**
 * Mapping from character symbol to its title/description.
 * Derived from SPECIAL_CHARACTERS to ensure consistency.
 */
export const CHAR_TO_TITLE: Record<string, string> = Object.fromEntries(
  SPECIAL_CHARACTERS.map(char => [char.symbol, char.title])
);
